import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { X } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  ELEVATION,
  TYPOGRAPHY,
  Z_INDEX,
  withWeight,
  durations,
} from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { useTabBarClearance } from '../../hooks/useTabBarClearance';
import { useWebHover } from '../../hooks/useWebHover';
import { useLeavingPanel } from '../../hooks/useLeavingPanel';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useEscapeToClose } from '../../utils/escapeStack';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { DialogPortal } from './DialogPortal';

/**
 * Global toast host, mounted once in app/_layout.tsx. Reads uiStore.toast —
 * one toast at a time, a new one replaces whatever is showing — so any
 * screen fires one with `useUIStore.getState().showToast(...)` instead of
 * mounting its own. Bottom-center on the desktop shell; above the floating
 * tab bar everywhere else (mirrors SaveAccountToast's placement).
 *
 * Escape closes it, but only when it's the topmost layer: it registers on
 * the same LIFO stack (escapeStack.ts) a sheet or dialog does, so opening
 * one over a toast makes Escape close that instead, same as two sheets
 * stacking.
 */
export function ToastHost() {
  const { colors } = useTheme();
  const liveToast = useUIStore((s) => s.toast);
  // Exit: a dismissed toast fades and drops EXIT_DROP_PX over durations.quick
  // before unmounting. A different toast replacing the current one keeps
  // `open` true, so it swaps at once with no exit.
  const leave = useLeavingPanel(liveToast != null, liveToast);
  const toast = leave.lastContent ?? null;
  const reducedMotion = useReducedMotion();
  const exit = useRef(new Animated.Value(0)).current;
  const dismissToast = useUIStore((s) => s.dismissToast);
  const isDesktopShell = useDesktopShell();
  const tabBarClearance = useTabBarClearance();
  const { hovered, hoverProps } = useWebHover();

  const toastId = liveToast?.id ?? null;
  const durationMs = liveToast?.durationMs ?? 0;

  useEffect(() => {
    if (leave.shown) {
      exit.setValue(0);
      return;
    }
    const animation = Animated.timing(exit, {
      toValue: 1,
      duration: reducedMotion ? 0 : durations.quick,
      easing: EXIT_EASING,
      useNativeDriver: Platform.OS !== 'web',
    });
    animation.start(({ finished }) => {
      if (finished) leave.onExited();
    });
    return () => animation.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leave.shown, exit, reducedMotion]);

  useEscapeToClose(!!liveToast, () => {
    if (toastId) dismissToast(toastId);
  });

  // Countdown line along the bottom edge, for toasts with an action (they
  // are the ones worth racing). Full at 0, empty at 1. It restarts and
  // holds full while hovered, the same as the dismiss timer below, since
  // that timer also starts over from its full duration on hover-out.
  const countdown = useRef(new Animated.Value(0)).current;
  const hasAction = !!liveToast?.action;
  useEffect(() => {
    countdown.setValue(0);
    if (!toastId || !hasAction || hovered || reducedMotion) return;
    const animation = Animated.timing(countdown, {
      toValue: 1,
      duration: durationMs,
      easing: Easing.linear,
      useNativeDriver: false,
    });
    animation.start();
    return () => animation.stop();
  }, [toastId, hasAction, durationMs, hovered, reducedMotion, countdown]);

  useEffect(() => {
    // Paused while the pointer hovers it on web (hovered is always false on
    // native — useWebHover is inert off web).
    if (!toastId || hovered) return;
    const timer = setTimeout(() => dismissToast(toastId), durationMs);
    return () => clearTimeout(timer);
  }, [toastId, durationMs, hovered, dismissToast]);

  if (!leave.mounted || !toast) return null;

  const handleAction = () => {
    toast.action?.onPress();
    dismissToast(toast.id);
  };

  // Portaled to <body> on web and fixed to the viewport: react-native-web
  // views each open a stacking context, so from inside the app tree no
  // z-index could lift the toast over a dialog's scrim (DialogPortal also
  // mounts dialogs on <body>). DialogPortal is a passthrough on native.
  return (
    <DialogPortal>
      <View
        pointerEvents="box-none"
        style={[
          styles.wrapper,
          Platform.OS === 'web' && WEB_FIXED,
          isDesktopShell ? styles.wrapperDesktop : { bottom: tabBarClearance + SPACING.md },
        ]}>
        <Animated.View
          pointerEvents={leave.shown ? undefined : 'none'}
          style={{
            opacity: exit.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }),
            transform: [
              {
                translateY: exit.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, EXIT_DROP_PX],
                }),
              },
            ],
          }}
          {...(leave.shown ? null : { dataSet: { rediLeaving: 'true' } })}>
          <View
            {...hoverProps}
            style={[styles.toast, ELEVATION.float, { backgroundColor: colors.action }]}
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
            testID="toast"
            // @ts-expect-error dataSet is a react-native-web prop (drives the
            // fade+rise keyframe in motion.ts's MOTION_CSS_VARS); not in RN's
            // type defs.
            dataSet={{ rediAnim: 'toast-in' }}>
            <Text style={[styles.message, { color: colors.actionOn }]} numberOfLines={2}>
              {toast.message}
            </Text>
            {toast.action && (
              <Pressable
                onPress={handleAction}
                accessibilityRole="button"
                accessibilityLabel={toast.action.label}
                hitSlop={8}
                style={[styles.actionButton, CURSOR_POINTER]}>
                <Text style={[styles.actionText, { color: colors.actionOn }]}>
                  {toast.action.label}
                </Text>
              </Pressable>
            )}
            <Pressable
              onPress={() => dismissToast(toast.id)}
              accessibilityRole="button"
              accessibilityLabel="Dismiss"
              hitSlop={8}
              style={[styles.closeButton, CURSOR_POINTER]}>
              <X size={14} color={colors.actionOn} />
            </Pressable>
            {toast.action && !reducedMotion && (
              <Animated.View
                pointerEvents="none"
                testID="toast-countdown"
                style={[
                  styles.countdown,
                  {
                    backgroundColor: colors.actionOn,
                    width: countdown.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['100%', '0%'],
                    }),
                  },
                ]}
              />
            )}
          </View>
        </Animated.View>
      </View>
    </DialogPortal>
  );
}

// Matches theme/motion.ts's `easings.exit`; see DockedPanel.tsx.
const EXIT_EASING = Easing.bezier(0.4, 0, 1, 1);
const EXIT_DROP_PX = 4;

// RN's ViewStyle has no 'fixed'; react-native-web passes it through.
const WEB_FIXED = { position: 'fixed' } as unknown as object;

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    left: SPACING.md,
    right: SPACING.md,
    alignItems: 'center',
    zIndex: Z_INDEX.toast,
  },
  wrapperDesktop: {
    bottom: SPACING.xl,
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 480,
    paddingVertical: SPACING.sm,
    paddingLeft: SPACING.md,
    paddingRight: SPACING.sm,
    borderRadius: BORDER_RADIUS.full,
    gap: SPACING.sm,
    overflow: 'hidden',
  },
  countdown: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    height: 1,
    opacity: 0.5,
  },
  message: {
    ...TYPOGRAPHY.body,
    flexShrink: 1,
  },
  actionButton: {
    paddingHorizontal: SPACING.xs,
    paddingVertical: SPACING.xxs,
  },
  actionText: {
    ...withWeight(TYPOGRAPHY.label, 'bold'),
    textDecorationLine: 'underline',
  },
  closeButton: {
    padding: SPACING.xxs,
  },
});
